import Order from './Order';
import OrderActivityLogEntry from './OrderActivityLogEntry';
import OrderBillingAddress from './OrderBillingAddress';
import OrderLoyaltyCoupon from './OrderLoyaltyCoupon';
import OrderPaymentInfo from './OrderPaymentInfo';
import OrderPromotion from './OrderPromotion';
import OrderShipment from './OrderShipment';
import OrderTermsAdjustment from './OrderTermsAdjustment';
import OrderedItem from './OrderedItem';
import OrderedItemFile from './OrderedItemFile';
import OrderedStockItem from './OrderedStockItem';
import TemplateEditSession from './TemplateEditSession';
import orderService from './orderService';
import orderedItemService from './orderedItemService';
import TemplatePreview from '../OrderItem/TemplatePreview';
import fileUploadService from '../FileUpload/fileUploadService';
import clientInputError from '../errors/clientInputError';
import PaginatedList from '../Pagination/PaginatedList';
import PaginationOptions from '../Pagination/PaginationOptions';
import RemotePaginatedList from '../Pagination/RemotePaginatedList';
import { typeCheck } from '../lib/util';

/*
 * OrderItemV2RestController#ORDER_IDS_LIMIT. Over this the server rejects the whole call, so the list is
 * checked here and reported as the same shape of input error the server would have produced.
 */

const ORDER_IDS_LIMIT = 100;

/*
 * Mirrors OrderV2RestController's own default (OrderDTO.CREATED_ON, DESC) rather than leaving it
 * implicit, so the documented default stays put if the controller's ever changes.
 */

const NEWEST_FIRST = {
    fieldName: 'createdOn',
    ascending: false,
};

/*
 * OfferingServiceType#STOCK. What the server branches on in generateReorderURL, and the only thing that
 * separates a stock line from a print one on an OrderedItem.
 */

const STOCK_SERVICE_TYPE = 'STOCK';

function toOrderPage (response, dataSource) {
    const { content, page } = response;

    return new PaginatedList(new RemotePaginatedList(content.map(order => new Order(order)), page, dataSource));
}

/**
 * SDK interface for reading a shopper's submitted orders.
 *
 * Everything here is scoped to the signed-in user. The listing and search endpoints return only orders
 * that user placed on this storefront; a single order can also be fetched when it was shared with them
 * through Collaterate, even though it would not appear in their own list.
 *
 * An order itself is read-only apart from {@link Collaterate.Order.rename rename} and
 * {@link Collaterate.Order.addActivityLogEntry addActivityLogEntry} - the rest of /orders/v2 (ledger
 * entries, financial adjustments, production changes) is admin-only and is not reachable from a
 * storefront session.
 *
 * The jobs on an order are not, though. A shopper may still approve or decline a proof, replace the
 * artwork, edit a templated design and reorder, and the methods that do those things are here too
 * because they are reached by order item id and scoped to the same user. They are the difference between
 * an order history a shopper can read and one they can act on.
 *
 * @namespace Collaterate.Order
 */

export default class OrderAPI {
    /**
     * Get a paginated list of the signed-in user's orders, newest first.
     *
     * @param {PaginatedList~paginationOptions} [paginationOptions] - Object of options to control paginated response.
     * @returns {Promise<PaginatedList>} Returns a Promise that resolves with a {@link PaginatedList PaginatedList}, whose items are {@link Order Order} objects.
     * @throws {CollaterateResponseError} If the user is not signed in.
     * @memberof Collaterate.Order
     * @example
     * // Render the shopper's order history, a page at a time
     * Collaterate.Order.getAll()
     *     .then(function (orders) {
     *         orders.items.forEach(function (order) {
     *             console.log(order.number, order.status, order.displayTotalPrice);
     *         });
     *
     *         if (orders.next) {
     *             return orders.next();
     *         }
     *     });
     */

    static async getAll (paginationOptions) {
        paginationOptions && typeCheck(paginationOptions, Object);

        const page = new PaginationOptions({ sort: NEWEST_FIRST, ...paginationOptions });

        return toOrderPage(await orderService.getAll(page), this.getAll.bind(this));
    }

    /**
     * Search the signed-in user's orders.
     *
     * A search term matches an order by its order number, its PO, its metadata, or by anything in one of
     * its items - job name, job number, product name or product id. There is no way to search a date
     * range; sort by `createdOn` and page instead.
     *
     * @param {Collaterate.Order~orderSearchOptions} [searchOptions] - Options object containing order search criteria.
     * @param {PaginatedList~paginationOptions} [paginationOptions] - Object of options to control paginated response.
     * @returns {Promise<PaginatedList>} Returns a Promise that resolves with a {@link PaginatedList PaginatedList}, whose items are {@link Order Order} objects.
     * @throws {CollaterateResponseError} If the user is not signed in.
     * @memberof Collaterate.Order
     * @example
     * // Find the shopper's orders that mention "business cards"
     * Collaterate.Order.search({ searchTerm: 'business cards' })
     *     .then(function (results) {
     *         console.log(results.totalItems + ' matching orders');
     *         results.items.forEach(function (order) {
     *             console.log(order.number, order.projectName);
     *         });
     *     });
     */

    static async search (searchOptions, paginationOptions) {
        searchOptions && typeCheck(searchOptions, Object);
        paginationOptions && typeCheck(paginationOptions, Object);

        const page = new PaginationOptions({ sort: NEWEST_FIRST, ...paginationOptions });

        return toOrderPage(await orderService.search(searchOptions || {}, page), this.search.bind(this, searchOptions));
    }

    /**
     * Get a single order.
     *
     * This resolves for an order the user placed and for one that was shared with them, which is wider
     * than what {@link Collaterate.Order.getAll getAll} lists.
     *
     * @param {number} orderId - The id of the order to fetch.
     * @returns {Promise<Order>} Returns a Promise that resolves with an {@link Order Order}.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getById(90210)
     *     .then(function (order) {
     *         console.log(order.number + ' placed ' + order.createdOn.toLocaleDateString());
     *         // What it is worth now, which an adjusted order reports separately from what it was submitted for
     *         console.log('Total: $' + order.adjustedTotals.displayTotalPrice);
     *     });
     */

    static async getById (orderId) {
        typeCheck(orderId, Number);

        return new Order(await orderService.getById(orderId));
    }

    /**
     * Get a paginated list of the jobs on an order.
     *
     * @param {number} orderId - The id of the order whose items to fetch.
     * @param {PaginatedList~paginationOptions} [paginationOptions] - Object of options to control paginated response.
     * @returns {Promise<PaginatedList>} Returns a Promise that resolves with a {@link PaginatedList PaginatedList}, whose items are {@link OrderedItem OrderedItem} objects.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getItems(90210)
     *     .then(function (items) {
     *         items.items.forEach(function (item) {
     *             console.log(item.name, item.displayQuantity, item.productionStatus);
     *         });
     *     });
     */

    static async getItems (orderId, paginationOptions) {
        typeCheck(orderId, Number);

        /*
         * async with nothing awaited trips require-await, which is a warning here on purpose (no-return-await
         * is the error). Keeping async is the point: a failed typeCheck then rejects, as it does on every
         * other method in this class, rather than throwing synchronously out of a promise-returning call.
         */
        return this.getItemsForOrders([orderId], paginationOptions);
    }

    /**
     * Get a paginated list of the jobs on several orders at once, so an order history page can fetch
     * every line it needs to show in one request rather than one per order.
     *
     * Each {@link OrderedItem OrderedItem} carries the `orderId` it belongs to, which is what to group
     * the results by. At most 100 ids may be requested at a time.
     *
     * By default the jobs come back grouped by order and then by job number. That default is the
     * server's, and it is a two-part sort that this list cannot reproduce once it starts paging - so
     * when fetching several orders at once, ask for a `pageSize` big enough to hold them all in one
     * page, or pass a sort of your own to page on.
     *
     * @param {number[]} orderIds - The ids of the orders whose items to fetch.
     * @param {PaginatedList~paginationOptions} [paginationOptions] - Object of options to control paginated response. Defaults to the server's own ordering: by order, then by job number.
     * @returns {Promise<PaginatedList>} Returns a Promise that resolves with a {@link PaginatedList PaginatedList}, whose items are {@link OrderedItem OrderedItem} objects.
     * @throws {TypeError} If orderIds is not an array.
     * @throws {CollaterateInputErrorList} If orderIds is empty or holds more than 100 ids.
     * @throws {CollaterateResponseError} If any of the orders does not exist, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * // Fetch a page of orders, then every job across them in a single follow-up call
     * Collaterate.Order.getAll()
     *     .then(function (orders) {
     *         var ids = orders.items.map(function (order) { return order.id; });
     *
     *         return Collaterate.Order.getItemsForOrders(ids, { pageSize: 100 });
     *     })
     *     .then(function (items) {
     *         items.items.forEach(function (item) {
     *             console.log('order ' + item.orderId + ': ' + item.name);
     *         });
     *     });
     */

    static async getItemsForOrders (orderIds, paginationOptions) {
        typeCheck(orderIds, Array);
        paginationOptions && typeCheck(paginationOptions, Object);

        if (!orderIds.length) {
            return Promise.reject(clientInputError([{
                field: 'orderIds',
                message: 'At least one order id is required.',
            }]));
        }

        if (orderIds.length > ORDER_IDS_LIMIT) {
            return Promise.reject(clientInputError([{
                field: 'orderIds',
                message: `No more than ${ORDER_IDS_LIMIT} order ids can be requested at once, received ${orderIds.length}.`,
            }]));
        }

        /*
         * No default sort is supplied here on purpose. The server's default for this endpoint is a pair
         * - order id, then job number - and PaginationOptions can only express a single sort, so naming
         * one would silently drop the job number tiebreak and scramble the jobs within each order.
         *
         * The same limitation bites paging: PaginationOptions rebuilds the request from the sort the
         * response echoed, and keeps only the primary, so page two of a multi-order fetch asks for
         * order id alone. Ask for every job in one page where you can - hence the pageSize hint in the
         * docs - or pass an explicit single sort that is unique enough to page on.
         */
        const page = new PaginationOptions(paginationOptions);
        const { content, page: pageMeta } = await orderService.getItems(orderIds, page);

        return new PaginatedList(new RemotePaginatedList(
            content.map(item => new OrderedItem(item)),
            pageMeta,
            this.getItemsForOrders.bind(this, orderIds)
        ));
    }

    /**
     * Get an order's shipments.
     *
     * This is the only place an order's full ship-to **street address** is available - an
     * {@link Order Order}'s own `shippingSummary` resolves the destination no further than country,
     * state and postal code, and describes only the first shipment while pricing all of them.
     *
     * An order ships in as many shipments as it needs, so expect more than one whenever items were
     * split across destinations or fulfilment centers. Tracking numbers appear here once the carrier
     * has them, per package as well as per shipment.
     *
     * @param {number} orderId - The id of the order whose shipments to fetch.
     * @returns {Promise<OrderShipment[]>} Returns a Promise that resolves with an array of {@link OrderShipment OrderShipment} objects. Empty if the order has no shipments, as a wholly digital order will not.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * // Show the shopper where each part of their order is going
     * Collaterate.Order.getShipments(90210)
     *     .then(function (shipments) {
     *         shipments.forEach(function (shipment) {
     *             console.log([
     *                 shipment.shipTo.name,
     *                 shipment.shipTo.address,
     *                 shipment.shipTo.city + ', ' + shipment.shipTo.stateAbbreviation + ' ' + shipment.shipTo.postalCode,
     *             ].join('\n'));
     *
     *             if (shipment.trackingNumber) {
     *                 console.log('Tracking ' + shipment.trackingNumber + ' via ' + shipment.vendorName);
     *             }
     *         });
     *     });
     */

    static async getShipments (orderId) {
        typeCheck(orderId, Number);

        const data = await orderService.getShipments(orderId);

        return (data || []).map(shipment => new OrderShipment(shipment));
    }

    /**
     * Get the promotion applied to an order.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<?OrderPromotion>} Returns a Promise that resolves with an {@link OrderPromotion OrderPromotion}, or with null if no promotion was used.
     * @throws {TypeError} If orderId is not a number.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getPromotion(90210)
     *     .then(function (promotion) {
     *         if (promotion) {
     *             console.log(promotion.name + ' saved $' + promotion.displayDiscountPrice);
     *         }
     *     });
     */

    static async getPromotion (orderId) {
        typeCheck(orderId, Number);

        const data = await orderService.getPromotion(orderId);

        return data ? new OrderPromotion(data) : null;
    }

    /**
     * Get the loyalty coupon redeemed against an order.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<?OrderLoyaltyCoupon>} Returns a Promise that resolves with an {@link OrderLoyaltyCoupon OrderLoyaltyCoupon}, or with null if no active coupon was redeemed.
     * @throws {TypeError} If orderId is not a number.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getLoyaltyCoupon(90210)
     *     .then(function (coupon) {
     *         if (coupon) {
     *             console.log('Coupon ' + coupon.code + ' took off $' + coupon.displayDiscountAmount);
     *         }
     *     });
     */

    static async getLoyaltyCoupon (orderId) {
        typeCheck(orderId, Number);

        const data = await orderService.getLoyaltyCoupon(orderId);

        return data ? new OrderLoyaltyCoupon(data) : null;
    }

    /**
     * Get an order's billing address, with the state and country resolved to names.
     *
     * An {@link Order Order} already carries a `billingAddress`, but only with ids for its state and
     * country. Use this when a receipt has to print the whole address.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<OrderBillingAddress>} Returns a Promise that resolves with an {@link OrderBillingAddress OrderBillingAddress}.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getBillingAddress(90210)
     *     .then(function (address) {
     *         console.log(address.address, address.city, address.stateName, address.postalCode);
     *     });
     */

    static async getBillingAddress (orderId) {
        typeCheck(orderId, Number);

        return new OrderBillingAddress(await orderService.getBillingAddress(orderId));
    }

    /**
     * Get the card details recorded against an order.
     *
     * Resolves with null for any order that was not paid by card - terms, PO and zero-total orders have
     * no card record at all, so null is a normal answer rather than a failure.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<?OrderPaymentInfo>} Returns a Promise that resolves with an {@link OrderPaymentInfo OrderPaymentInfo}, or with null if the order was not paid by card.
     * @throws {TypeError} If orderId is not a number.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getPaymentInfo(90210)
     *     .then(function (payment) {
     *         if (payment) {
     *             console.log('Paid with ' + payment.cardType + ' ending ' + payment.last4CardNumber);
     *         } else {
     *             console.log('Not a card order');
     *         }
     *     });
     */

    static async getPaymentInfo (orderId) {
        typeCheck(orderId, Number);

        const data = await orderService.getPaymentInfo(orderId);

        return data ? new OrderPaymentInfo(data) : null;
    }

    /**
     * Get the adjustments made to a terms (invoiced) order's balance after it was placed.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<OrderTermsAdjustment[]>} Returns a Promise that resolves with an array of {@link OrderTermsAdjustment OrderTermsAdjustment} objects, in the order they were applied. Empty if there were none.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getTermsAdjustments(90210)
     *     .then(function (adjustments) {
     *         adjustments.forEach(function (adjustment) {
     *             console.log(adjustment.reason + ': $' + adjustment.displayAdjustmentAmount);
     *         });
     *     });
     */

    static async getTermsAdjustments (orderId) {
        typeCheck(orderId, Number);

        const data = await orderService.getTermsAdjustments(orderId);

        return (data || []).map(adjustment => new OrderTermsAdjustment(adjustment));
    }

    /**
     * Get an order's activity log.
     *
     * Only the entries the signed-in user is allowed to see are returned - the internal notes
     * Collaterate staff keep against an order never reach a storefront.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<OrderActivityLogEntry[]>} Returns a Promise that resolves with an array of {@link OrderActivityLogEntry OrderActivityLogEntry} objects. Empty if there are none.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getActivityLog(90210)
     *     .then(function (entries) {
     *         entries.forEach(function (entry) {
     *             console.log(entry.createdOn.toLocaleString(), entry.title, entry.message);
     *         });
     *     });
     */

    static async getActivityLog (orderId) {
        typeCheck(orderId, Number);

        const data = await orderService.getActivityLog(orderId);

        return (data || []).map(entry => new OrderActivityLogEntry(entry));
    }

    /**
     * Add an entry to an order's activity log - how a shopper leaves a note about an order that
     * Collaterate staff will see.
     *
     * The entry is attributed to the signed-in user and cannot be edited or removed afterwards.
     *
     * @param {number} orderId - The id of the order to add the entry to.
     * @param {Collaterate.Order~activityLogEntry} entry - The entry to add.
     * @returns {Promise<OrderActivityLogEntry>} Returns a Promise that resolves with the created {@link OrderActivityLogEntry OrderActivityLogEntry}.
     * @throws {TypeError} If orderId is not a number, or entry is not an object.
     * @throws {CollaterateInputErrorList} If the entry has no title.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.addActivityLogEntry(90210, {
     *         title: 'Delivery question',
     *         message: 'Please leave this with the front desk.',
     *     })
     *     .then(function (entry) {
     *         console.log('Added at ' + entry.createdOn.toLocaleString());
     *     });
     */

    static async addActivityLogEntry (orderId, entry) {
        typeCheck(orderId, Number);
        typeCheck(entry, Object);

        if (!entry.title || !`${entry.title}`.trim()) {
            return Promise.reject(clientInputError([{
                field: 'title',
                message: 'A title is required.',
            }]));
        }

        return new OrderActivityLogEntry(await orderService.addActivityLogEntry(orderId, {
            title: `${entry.title}`,
            message: entry.message ? `${entry.message}` : '',
        }));
    }

    /**
     * Rename an order.
     *
     * The name is the one the shopper gave the project this order was placed from, and it is the only
     * thing about a submitted order a storefront can change. It is cosmetic - nothing about pricing,
     * production or fulfilment moves with it.
     *
     * The server strips HTML from the name, so the value that comes back is what was actually stored and
     * may differ from what was sent. Use it rather than the string you passed in.
     *
     * @param {number} orderId - The id of the order to rename.
     * @param {string} name - The new name. Cannot be blank.
     * @returns {Promise<Collaterate.Order~renamedOrder>} Returns a Promise that resolves with the order's id and its stored name.
     * @throws {TypeError} If orderId is not a number, or name is not a string.
     * @throws {CollaterateInputErrorList} If name is blank.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.rename(90210, 'Spring catalog reprint')
     *     .then(function (renamed) {
     *         document.querySelector('#order-name').textContent = renamed.name;
     *     });
     */

    static async rename (orderId, name) {
        typeCheck(orderId, Number);
        typeCheck(name, String);

        if (!name.trim()) {
            return Promise.reject(clientInputError([{
                field: 'projectName',
                message: 'An order name is required.',
            }]));
        }

        const data = await orderService.patchOrder(orderId, { projectName: name });

        return Object.freeze({
            id: data.id,
            name: data.projectName,
        });
    }

    /**
     * Get the url that starts a reorder of a job, for the "order this again" link on an order history
     * page.
     *
     * The url is a path on this storefront - send the shopper to it and the product page picks up from
     * there, with a stock item landing on its product page and a print item arriving pre-configured from
     * the original job. It is not an action: nothing is added to a cart until the shopper works through
     * the page it leads to.
     *
     * Takes the item rather than an id because the server needs to know whether it is a stock item, and
     * gets that wrong quietly - ask for a print item as stock and it hands back that product's plain
     * marketing page with none of the original configuration, which looks like a working link. The item
     * carries what settles it, so this reads it off `serviceType` instead of asking.
     *
     * Check `item.canReorder` first. An item is ineligible when its price came from a custom quote, and
     * calling this for one fails rather than returning a url.
     *
     * @param {OrderedItem} item - The job to reorder.
     * @param {Collaterate.Order~reorderOptions} [options] - Object of options.
     * @returns {Promise<string>} Returns a Promise that resolves with a site-relative url to send the shopper to.
     * @throws {TypeError} If item is not an OrderedItem.
     * @throws {CollaterateInputErrorList} If the item cannot be reordered.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * // Wire up a reorder button for every eligible job on an order
     * Collaterate.Order.getItems(90210)
     *     .then(function (items) {
     *         items.items.filter(function (item) { return item.canReorder; }).forEach(function (item) {
     *             Collaterate.Order.getReorderUrl(item).then(function (url) {
     *                 document.querySelector('#reorder-' + item.id).href = url;
     *             });
     *         });
     *     });
     */

    static async getReorderUrl (item, options = {}) {
        /*
         * async with nothing awaited trips require-await, as it does on getItems above, and for the same
         * reason: keeping async is what makes a failed typeCheck reject rather than throw synchronously
         * out of a promise-returning call.
         */
        typeCheck(item, OrderedItem);
        typeCheck(options, Object);

        if (!item.canReorder) {
            return Promise.reject(clientInputError([{
                field: 'orderItemId',
                message: 'This item cannot be reordered.',
            }]));
        }

        /*
         * reuseFiles defaults to true because the artwork is the whole point of a reorder - a shopper
         * reordering business cards wants the same cards. Pass false for the "same product, new artwork"
         * path. It is ignored for a stock item, which has no artwork.
         */
        const reuseFiles = options.reuseFiles !== false;
        const isStock = item.serviceType === STOCK_SERVICE_TYPE;

        return orderedItemService.getReorderUrl(item.id, reuseFiles, isStock);
    }

    /**
     * Get the stock (warehoused, non-printed) lines on an order, with the variant each was ordered as.
     *
     * There are exactly two reasons to call this, and an order history that needs neither should not:
     *
     * 1. **A live stock level.** `variant.availableInventory` is how many are in the warehouse *now*,
     *    read at call time rather than recorded at order time. It is the only place in this SDK that
     *    exposes inventory at all, so a "2,400 in stock" note, or a reorder button disabled because the
     *    product has since sold out, can only be built from here. Pairs with
     *    {@link Collaterate.Order.getReorderUrl getReorderUrl}, which for a stock item leads to the
     *    product page. `variant.price` is likewise the variant's price today, which is worth comparing
     *    against what the shopper was charged if you want to warn that it has changed.
     * 2. **Stock components of a product set.** A stock line that belongs to a product set is the one
     *    thing {@link Collaterate.Order.getItems getItems} leaves out - it hides those because the set's
     *    primary line stands in for them. This returns them, so it is the only way to itemise a set.
     *
     * **It is not a second list to append to `getItems`** - append it and every standalone stock line
     * appears twice. Those lines are already there. Join on `id` instead, which is the same id space.
     *
     * Which variant the shopper chose stays on the {@link OrderedItem OrderedItem}, as
     * `stockVariantDetails`. This call adds to that line rather than restating it.
     *
     * @param {number} orderId - The id of the order.
     * @returns {Promise<OrderedStockItem[]>} Returns a Promise that resolves with an array of {@link OrderedStockItem OrderedStockItem} objects. Empty when the order has no stock lines, which is the common case.
     * @throws {TypeError} If orderId is not a number.
     * @throws {CollaterateResponseError} If no such order exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * // Show live stock levels against the lines an order history is already rendering
     * Promise.all([
     *         Collaterate.Order.getItems(90210),
     *         Collaterate.Order.getStockItems(90210),
     *     ])
     *     .then(function (results) {
     *         var stockById = {};
     *
     *         results[1].forEach(function (stockItem) {
     *             stockById[stockItem.id] = stockItem;
     *         });
     *
     *         results[0].items.forEach(function (item) {
     *             var stock = stockById[item.id];
     *
     *             if (stock && stock.variant) {
     *                 console.log(item.name + ': ' + stock.variant.displayAvailableInventory + ' in stock');
     *             }
     *         });
     *     });
     */

    static async getStockItems (orderId) {
        typeCheck(orderId, Number);

        const data = await orderedItemService.getStockItems(orderId);

        return (data || []).map(item => new OrderedStockItem(item));
    }

    /**
     * Get the artwork files on a job.
     *
     * A job can hold several, of which at most one is active - the active one is what gets printed. The
     * rest are superseded uploads kept for history.
     *
     * @param {number} orderItemId - The id of the job, i.e. an {@link OrderedItem OrderedItem}'s `id`.
     * @returns {Promise<OrderedItemFile[]>} Returns a Promise that resolves with an array of {@link OrderedItemFile OrderedItemFile} objects, each with `type` `"PRINT"`. Empty if nothing was uploaded.
     * @throws {TypeError} If orderItemId is not a number.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getPrintFiles(4242)
     *     .then(function (files) {
     *         files.filter(function (file) { return file.isActive; }).forEach(function (file) {
     *             console.log(file.name, file.downloadUrl);
     *         });
     *     });
     */

    static async getPrintFiles (orderItemId) {
        typeCheck(orderItemId, Number);

        const data = await orderedItemService.getPrintFiles(orderItemId);

        return (data || []).map(file => new OrderedItemFile(file, 'PRINT'));
    }

    /**
     * Upload a new artwork file to a job on a submitted order.
     *
     * This is the post-order twin of
     * {@link Collaterate.Assets.uploadOrderItemPrintFiles Assets.uploadOrderItemPrintFiles}, which works
     * on a project line and cannot address an ordered one. Both stage the bytes to S3 the same way; only
     * the thing they attach to differs.
     *
     * The new file is **not** made active by the upload. Call
     * {@link Collaterate.Order.activatePrintFile activatePrintFile} with the returned file to put it into
     * production, and {@link Collaterate.Order.deactivatePrintFile deactivatePrintFile} on the one it
     * replaces - uploading alone changes nothing about what gets printed.
     *
     * Whether the job is still at a stage where replacing artwork achieves anything is a production
     * question the API will not answer: a file uploaded against a job already on press is accepted and
     * has no effect. Gate on `productionStatus` before offering this.
     *
     * @param {number} orderItemId - The id of the job to attach the file to.
     * @param {File} file - The file to upload.
     * @param {function} [onProgress] - Called with an axios progress event as the bytes go to S3.
     * @returns {Promise<OrderedItemFile>} Returns a Promise that resolves with the created {@link OrderedItemFile OrderedItemFile}.
     * @throws {TypeError} If orderItemId is not a number, or file is not a File.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * // Replace a job's artwork after ordering
     * Collaterate.Order.uploadPrintFile(4242, input.files[0])
     *     .then(function (file) {
     *         return Collaterate.Order.activatePrintFile(4242, file.id);
     *     })
     *     .then(function () {
     *         console.log('New artwork is now the one that will print.');
     *     });
     */

    static async uploadPrintFile (orderItemId, file, onProgress) {
        typeCheck(orderItemId, Number);
        typeCheck(file, File);

        const stagedFileReservationName = await fileUploadService.stageFile(file, onProgress);

        return this.addPrintFile(orderItemId, stagedFileReservationName);
    }

    /**
     * Attach an already-staged file to a job as artwork.
     *
     * Use this when the bytes were staged separately with
     * {@link Collaterate.Assets.stageFile Assets.stageFile} - to upload before the shopper has chosen
     * which job the file is for, say. {@link Collaterate.Order.uploadPrintFile uploadPrintFile} does both
     * halves in one call and is what most themes want.
     *
     * As with `uploadPrintFile`, the file arrives inactive. See that method for what else has to happen.
     *
     * @param {number} orderItemId - The id of the job to attach the file to.
     * @param {string} stagedFileReservationName - The reservation name {@link Collaterate.Assets.stageFile Assets.stageFile} resolved with.
     * @returns {Promise<OrderedItemFile>} Returns a Promise that resolves with the created {@link OrderedItemFile OrderedItemFile}.
     * @throws {TypeError} If orderItemId is not a number, or stagedFileReservationName is not a string.
     * @throws {CollaterateInputErrorList} If stagedFileReservationName is blank, or names a reservation the server cannot find.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Assets.stageFile(input.files[0])
     *     .then(function (reservationName) {
     *         return Collaterate.Order.addPrintFile(4242, reservationName);
     *     });
     */

    static async addPrintFile (orderItemId, stagedFileReservationName) {
        typeCheck(orderItemId, Number);
        typeCheck(stagedFileReservationName, String);

        if (!stagedFileReservationName.trim()) {
            return Promise.reject(clientInputError([{
                field: 'stagedFileReservationName',
                message: 'A staged file reservation name is required.',
            }]));
        }

        return new OrderedItemFile(
            await orderedItemService.addPrintFile(orderItemId, stagedFileReservationName),
            'PRINT'
        );
    }

    /**
     * Make an artwork file the one that will be printed.
     *
     * The server does not deactivate the others, so activating a second file leaves a job with two active
     * files and no statement of which wins. Deactivate the outgoing one.
     *
     * @param {number} orderItemId - The id of the job the file belongs to.
     * @param {number} printFileId - The id of the file to activate.
     * @returns {Promise<void>} Returns a Promise that resolves once the file is active. Re-read the files to see the change.
     * @throws {TypeError} If either argument is not a number.
     * @throws {CollaterateInputErrorList} If no such file exists.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.deactivatePrintFile(4242, oldFile.id)
     *     .then(function () {
     *         return Collaterate.Order.activatePrintFile(4242, newFile.id);
     *     });
     */

    static async activatePrintFile (orderItemId, printFileId) {
        typeCheck(orderItemId, Number);
        typeCheck(printFileId, Number);

        await orderedItemService.activatePrintFile(orderItemId, printFileId);
    }

    /**
     * Take an artwork file out of use without deleting it.
     *
     * @param {number} orderItemId - The id of the job the file belongs to.
     * @param {number} printFileId - The id of the file to deactivate.
     * @returns {Promise<void>} Returns a Promise that resolves once the file is inactive.
     * @throws {TypeError} If either argument is not a number.
     * @throws {CollaterateInputErrorList} If no such file exists.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.deactivatePrintFile(4242, file.id);
     */

    static async deactivatePrintFile (orderItemId, printFileId) {
        typeCheck(orderItemId, Number);
        typeCheck(printFileId, Number);

        await orderedItemService.deactivatePrintFile(orderItemId, printFileId);
    }

    /**
     * Set the note on an artwork file - what the shopper wants Collaterate to know about this particular
     * file.
     *
     * The note is the only thing about an uploaded file that can be changed. Pass an empty string to
     * clear it.
     *
     * @param {number} orderItemId - The id of the job the file belongs to.
     * @param {number} printFileId - The id of the file to annotate.
     * @param {string} note - The note. Empty clears it.
     * @returns {Promise<OrderedItemFile>} Returns a Promise that resolves with the updated {@link OrderedItemFile OrderedItemFile}.
     * @throws {TypeError} If orderItemId or printFileId is not a number, or note is not a string.
     * @throws {CollaterateInputErrorList} If no such file exists.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.setPrintFileNote(4242, file.id, 'Use the version with the updated logo.')
     *     .then(function (updated) {
     *         console.log(updated.note);
     *     });
     */

    static async setPrintFileNote (orderItemId, printFileId, note) {
        typeCheck(orderItemId, Number);
        typeCheck(printFileId, Number);
        typeCheck(note, String);

        return new OrderedItemFile(
            await orderedItemService.patchPrintFile(orderItemId, printFileId, { note }),
            'PRINT'
        );
    }

    /**
     * Get the proof files produced for a job - what the shopper is being asked to approve.
     *
     * Proofs are produced by Collaterate, so unlike artwork there is nothing here to upload or change
     * from a storefront. The shopper's only move is
     * {@link Collaterate.Order.approveProof approveProof} or
     * {@link Collaterate.Order.declineProof declineProof}.
     *
     * @param {number} orderItemId - The id of the job.
     * @returns {Promise<OrderedItemFile[]>} Returns a Promise that resolves with an array of {@link OrderedItemFile OrderedItemFile} objects, each with `type` `"PROOF"`. Empty until a proof has been produced.
     * @throws {TypeError} If orderItemId is not a number.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getProofFiles(4242)
     *     .then(function (proofs) {
     *         proofs.forEach(function (proof) {
     *             console.log(proof.name, proof.downloadUrl);
     *         });
     *     });
     */

    static async getProofFiles (orderItemId) {
        typeCheck(orderItemId, Number);

        const data = await orderedItemService.getProofFiles(orderItemId);

        return (data || []).map(file => new OrderedItemFile(file, 'PROOF'));
    }

    /**
     * Get the rendered proof imagery for a job - page thumbnails a shopper can look at in the browser,
     * as an alternative to downloading the proof file itself.
     *
     * This is relayed verbatim from a separate imaging service rather than being a Collaterate resource,
     * so the SDK deliberately does not model it: the service owns the shape and adds to it, and parsing
     * it here would drop whatever it adds next. Branch on `status`:
     *
     * - `"ready"` - imagery is available on the returned object.
     * - `"pending"` - a render is in flight; ask again shortly.
     * - `"none"` - there is nothing to show. `reason: "unavailable"` means the service could not be
     *   reached or is switched off for this deployment, which is not an error the shopper should see.
     *
     * It never rejects for an unavailable visual - the relay turns every upstream failure into
     * `status: "none"` - so a theme can call it unconditionally and show a placeholder.
     *
     * @param {number} orderItemId - The id of the job.
     * @returns {Promise<Object>} Returns a Promise that resolves with the imaging service's response, frozen. Always carries a `status`.
     * @throws {TypeError} If orderItemId is not a number.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getProofVisual(4242)
     *     .then(function (visual) {
     *         if (visual.status === 'ready') {
     *             renderProofPages(visual);
     *         } else if (visual.status === 'pending') {
     *             showSpinner();
     *         } else {
     *             showDownloadLinkInstead();
     *         }
     *     });
     */

    static async getProofVisual (orderItemId) {
        typeCheck(orderItemId, Number);

        const visual = await orderedItemService.getProofVisual(orderItemId);

        return Object.freeze(visual || { status: 'none' });
    }

    /**
     * Approve the proof on a job, releasing it to production.
     *
     * Show the item's `proofTerms` before calling this - approving is the shopper accepting them, and on
     * a distributed job it also locks the job against further change.
     *
     * @param {number} orderItemId - The id of the job.
     * @param {string} [comment] - An optional comment, recorded on the order's activity log with the approval.
     * @returns {Promise<void>} Returns a Promise that resolves once the proof is approved. Re-read the item to see its new `proofStatus`.
     * @throws {TypeError} If orderItemId is not a number, or comment is given and is not a string.
     * @throws {CollaterateInputErrorList} If the item is cancelled, or its proof is already approved.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.approveProof(4242, 'Looks good, please print.')
     *     .then(function () {
     *         return Collaterate.Order.getItems(90210);
     *     });
     */

    static async approveProof (orderItemId, comment = '') {
        typeCheck(orderItemId, Number);
        typeCheck(comment, String);

        await orderedItemService.approveProof(orderItemId, comment);
    }

    /**
     * Decline the proof on a job, sending it back for correction.
     *
     * The reason is required and must be one the supplier configured: read the options off the item's
     * `proofDenialReasons` and pass the id of the one the shopper picked. Anything else is rejected.
     *
     * The reason's name and the comment are joined into a single activity log entry, so the comment is
     * where the shopper says what is actually wrong. It is optional but rarely worth omitting.
     *
     * @param {number} orderItemId - The id of the job.
     * @param {number} denialReasonId - The id of the reason, from the item's `proofDenialReasons`.
     * @param {string} [comment] - What is wrong with the proof. Recorded alongside the reason.
     * @returns {Promise<void>} Returns a Promise that resolves once the proof is declined.
     * @throws {TypeError} If orderItemId or denialReasonId is not a number, or comment is given and is not a string.
     * @throws {CollaterateInputErrorList} If the reason id is not a configured one, the item is cancelled, or its proof is already declined.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * // Build the picker from the item, then decline with what the shopper chose
     * var reasons = item.proofDenialReasons;
     *
     * Collaterate.Order.declineProof(item.id, reasons[0].id, 'The address on the back is out of date.');
     */

    static async declineProof (orderItemId, denialReasonId, comment = '') {
        typeCheck(orderItemId, Number);
        typeCheck(denialReasonId, Number);
        typeCheck(comment, String);

        await orderedItemService.declineProof(orderItemId, denialReasonId, comment);
    }

    /**
     * Get page previews of the personalized artwork on a templated job.
     *
     * The ordered-item counterpart of
     * {@link Collaterate.Project.getItemTemplatePreview Project.getItemTemplatePreview}, which addresses
     * a line in a project and cannot see one that has been ordered.
     *
     * @param {number} orderItemId - The id of the job.
     * @param {Collaterate.Order~templatePreviewOptions} [options] - Object of options.
     * @returns {Promise<TemplatePreview>} Returns a Promise that resolves with a {@link TemplatePreview TemplatePreview}.
     * @throws {TypeError} If orderItemId is not a number.
     * @throws {CollaterateResponseError} If no such item exists, the user cannot see it, or it is not templated.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.getItemTemplatePreview(4242, { maxPixelDimension: 400 })
     *     .then(function (preview) {
     *         preview.pages.forEach(function (page) {
     *             addImage(page.thumbnailUrl, page.largeUrl);
     *         });
     *     });
     */

    static async getItemTemplatePreview (orderItemId, options = {}) {
        typeCheck(orderItemId, Number);
        typeCheck(options, Object);

        const params = {};

        if (typeof options.maxPixelDimension !== 'undefined') {
            params.maxPixelDimension = options.maxPixelDimension;
        }

        if (typeof options.includeThumbnails !== 'undefined') {
            params.includeThumbnails = Boolean(options.includeThumbnails);
        }

        return new TemplatePreview(await orderedItemService.getTemplatePreview(orderItemId, params));
    }

    /**
     * Start an editing session for the personalized artwork on a templated job, so a shopper can fix a
     * typo without reordering.
     *
     * Returns everything needed to host the template vendor's editor. Read
     * {@link TemplateEditSession TemplateEditSession} before using it - whether the edit needs a
     * follow-up {@link Collaterate.Order.saveTemplateEdit saveTemplateEdit} call depends on the vendor,
     * and getting that wrong loses the shopper's work silently.
     *
     * Each call starts a *new* session. Whether the shopper resumes where they left off or begins from
     * the original template is `startOver`.
     *
     * @param {number} orderItemId - The id of the job.
     * @param {Collaterate.Order~templateEditOptions} [options] - Object of options.
     * @returns {Promise<TemplateEditSession>} Returns a Promise that resolves with a {@link TemplateEditSession TemplateEditSession}.
     * @throws {TypeError} If orderItemId is not a number.
     * @throws {CollaterateResponseError} If no such item exists, the user cannot see it, or it is not templated.
     * @memberof Collaterate.Order
     * @example
     * Collaterate.Order.startTemplateEdit(4242)
     *     .then(function (session) {
     *         var frame = document.querySelector('#editor');
     *
     *         frame.src = session.url;
     *         frame.width = session.width;
     *         frame.height = session.height;
     *     });
     */

    static async startTemplateEdit (orderItemId, options = {}) {
        typeCheck(orderItemId, Number);
        typeCheck(options, Object);

        return new TemplateEditSession(
            await orderedItemService.startTemplateEdit(orderItemId, Boolean(options.startOver))
        );
    }

    /**
     * Commit an editing session started by
     * {@link Collaterate.Order.startTemplateEdit startTemplateEdit}.
     *
     * Only needed when that session came back with `postbackRequired` true - for those vendors nothing is
     * saved until this runs. Call it with the session code the editor hands back when the shopper
     * finishes, then re-read {@link Collaterate.Order.getItemTemplatePreview getItemTemplatePreview} to
     * show the result.
     *
     * @param {number} orderItemId - The id of the job.
     * @param {string} podTemplateSessionCode - The session code the template editor produced.
     * @returns {Promise<void>} Returns a Promise that resolves once the edit is saved.
     * @throws {TypeError} If orderItemId is not a number, or podTemplateSessionCode is not a string.
     * @throws {CollaterateInputErrorList} If podTemplateSessionCode is blank.
     * @throws {CollaterateResponseError} If no such item exists, or the user cannot see it.
     * @memberof Collaterate.Order
     * @example
     * if (session.postbackRequired) {
     *     Collaterate.Order.saveTemplateEdit(4242, sessionCode)
     *         .then(function () {
     *             return Collaterate.Order.getItemTemplatePreview(4242);
     *         })
     *         .then(renderPreview);
     * }
     */

    static async saveTemplateEdit (orderItemId, podTemplateSessionCode) {
        typeCheck(orderItemId, Number);
        typeCheck(podTemplateSessionCode, String);

        if (!podTemplateSessionCode.trim()) {
            return Promise.reject(clientInputError([{
                field: 'podTemplateSessionCode',
                message: 'A template session code is required.',
            }]));
        }

        /*
         * The response is an OrderItemDTO - the *project* line shape, returned even for an ordered item,
         * because this handler is shared with the pre-order editing path. It describes the item as the
         * cart would, not as an order does, so it is dropped rather than handed back as something it is
         * not. Re-read the previews for the result of the edit.
         */
        await orderedItemService.saveTemplateEdit(orderItemId, podTemplateSessionCode);
    }
}

/**
 * Order search criteria.
 *
 * @typedef {Object} Collaterate.Order~orderSearchOptions
 * @property {string} [searchTerm] - Matches an order by order number, PO or metadata, or by the job name, job number, product name or product id of any of its items.
 * @property {boolean} [digitalOnly] - When true, only return orders made up entirely of digital items.
 */

/**
 * A new activity log entry.
 *
 * @typedef {Object} Collaterate.Order~activityLogEntry
 * @property {string} title - The entry's title. Required.
 * @property {string} [message] - The entry's body.
 */

/**
 * The result of renaming an order.
 *
 * @typedef {Object} Collaterate.Order~renamedOrder
 * @property {number} id - The order's id.
 * @property {string} name - The name as stored, after the server stripped any HTML from it.
 */

/**
 * Options for building a reorder url.
 *
 * @typedef {Object} Collaterate.Order~reorderOptions
 * @property {boolean} [reuseFiles=true] - Whether the reorder starts with the original job's artwork. Pass false to make the shopper upload new files. Ignored for a stock item.
 */

/**
 * Options for previewing a templated job.
 *
 * @typedef {Object} Collaterate.Order~templatePreviewOptions
 * @property {number} [maxPixelDimension=900] - The longest edge, in pixels, of the generated `largeUrl` image.
 * @property {boolean} [includeThumbnails=true] - Whether to generate `thumbnailUrl` images as well as large ones.
 */

/**
 * Options for starting a template editing session.
 *
 * @typedef {Object} Collaterate.Order~templateEditOptions
 * @property {boolean} [startOver=false] - When true the shopper starts from the original template, discarding the personalization already on the job. When false they pick up from what is there.
 */
